import {
  BlockRuleAddresses,
  BlockRuleBans,
  BlockRuleChangeResponse,
  BlockRulePreview,
  BlockRulesLayer,
  ServerBlockRules,
  type BlockRule,
  type ConfigScopeRef,
} from '@bartizo/shared';
import { apiRequest } from './client.js';

/**
 * Block rules (knowledge doc section 12, "Block rules"; P11.5). Each change is one
 * rule, saved by its id and site or removed — never a layer's list sent back
 * whole, so two people changing two rules both get their way.
 */

const query = (ref: ConfigScopeRef): string =>
  ref.scope === 'global' ? '?scope=global' : `?scope=${ref.scope}&id=${encodeURIComponent(ref.id)}`;

export const getBlockRules = (ref: ConfigScopeRef): Promise<BlockRulesLayer> =>
  apiRequest(`/block-rules${query(ref)}`, { schema: BlockRulesLayer });

export const getBlockRuleBans = (): Promise<BlockRuleBans> =>
  apiRequest('/block-rules/bans', { schema: BlockRuleBans });

export const getBlockRuleAddresses = (rule: string, cursor?: string): Promise<BlockRuleAddresses> =>
  apiRequest(
    `/block-rules/addresses?rule=${encodeURIComponent(rule)}${
      cursor === undefined ? '' : `&cursor=${encodeURIComponent(cursor)}`
    }`,
    { schema: BlockRuleAddresses },
  );

export const saveBlockRule = (
  scope: ConfigScopeRef,
  rule: BlockRule,
): Promise<BlockRuleChangeResponse> =>
  apiRequest('/block-rules', {
    method: 'PUT',
    body: { scope, rule },
    schema: BlockRuleChangeResponse,
  });

export const removeBlockRule = (
  scope: ConfigScopeRef,
  rule: { id: string; site?: string | undefined },
): Promise<BlockRuleChangeResponse> =>
  apiRequest('/block-rules/remove', {
    method: 'POST',
    body: { scope, id: rule.id, ...(rule.site === undefined ? {} : { site: rule.site }) },
    schema: BlockRuleChangeResponse,
  });

export const previewBlockRule = (
  scope: ConfigScopeRef,
  rule: BlockRule,
): Promise<BlockRulePreview> =>
  apiRequest('/block-rules/preview', {
    method: 'POST',
    body: { scope, rule },
    schema: BlockRulePreview,
  });

export const getServerBlockRules = (id: string): Promise<ServerBlockRules> =>
  apiRequest(`/servers/${encodeURIComponent(id)}/block-rules`, { schema: ServerBlockRules });

/** A server's or a group's rule moved to a wider layer (P11.6). */
export const promoteBlockRule = (
  from: ConfigScopeRef,
  to: ConfigScopeRef,
  rule: { id: string; site?: string | undefined },
): Promise<BlockRuleChangeResponse> =>
  apiRequest('/block-rules/promote', {
    method: 'POST',
    body: { from, to, id: rule.id, ...(rule.site === undefined ? {} : { site: rule.site }) },
    schema: BlockRuleChangeResponse,
  });
